import { modals } from '@mantine/modals';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ChecklistSubmissionForm } from 'src/Components/Forms/ChecklistSubmission/ChecklistSubmissionForm';
import {
  SubmitChecklistFormValues,
  useSubmitChecklistForm,
} from 'src/Hooks/Forms/useSubmitChecklist';
import { useSubmitChecklist } from 'src/Hooks/Mutations/useSubmitChecklist';
import useFetchChecklist from 'src/Hooks/Queries/useFetchChecklist';
import { CreateChecklistSubmissionDto } from 'src/Services/API/generated';

import { Props } from './ChecklistSubmissionCreationFormWrapper.types';
const ChecklistSubmissionCreationFormWrapper = ({ checklistId, onCreate, onCancel }: Props) => {
  const form = useSubmitChecklistForm();
  const { data: checklist } = useFetchChecklist(checklistId);
  const { t } = useTranslation();

  const fillFormWithDefaults = useCallback(() => {
    if (checklist) {
      form.setValues({
        items: checklist.items.map(item => ({
          checklistItemId: item.id,
          isChecked: false,
        })),
        durationMinutes: '',
        globalComment: '',
      });
    } else {
      form.setValues({ items: [], durationMinutes: '', globalComment: '' });
    }
  }, [checklist, form.setValues]);

  const { mutate } = useSubmitChecklist({
    onSuccess: () => {
      onCreate();
      modals.openContextModal({
        modal: 'info',
        title: t('checklist.submission.successTitle', 'Soumission réussie'),
        innerProps: {
          content: t(
            'checklist.submission.successMessage',
            'Vos réponses ont été enregistrées avec succès.'
          ),
          confirmText: t('common.ok', 'Ok'),
        },
      });
      form.reset();
      fillFormWithDefaults();
    },
  });

  useEffect(() => {
    fillFormWithDefaults();
  }, [fillFormWithDefaults]);

  const handleSubmit = (values: SubmitChecklistFormValues) => {
    const trimmedComment = values.globalComment.trim();
    const payload: CreateChecklistSubmissionDto = {
      items: values.items,
      durationSeconds:
        typeof values.durationMinutes === 'number' && values.durationMinutes > 0
          ? values.durationMinutes * 60
          : undefined,
      globalComment: trimmedComment.length > 0 ? trimmedComment : undefined,
    };
    mutate({ checklist: payload, checklistId });
  };

  return (
    <ChecklistSubmissionForm
      onCancel={onCancel}
      form={form}
      onSubmit={form.onSubmit(handleSubmit)}
      submitButtonLabel={t('checklist.form.submitAnswers', 'Soumettre les réponses')}
      checklist={checklist}
    />
  );
};

export default ChecklistSubmissionCreationFormWrapper;
